import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Clock, Eye, User, Share2, Copy } from 'lucide-react';
import styles from '../WikiPage.module.css';

const defaultFormatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('ru-RU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const TelegramIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

export const ShareButton = ({ shareUrl, title, className }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const menuRef = useRef(null);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (!menuOpen) return;
    const handleOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) closeMenu();
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [menuOpen, closeMenu]);

  const copyLink = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setFeedback('Ссылка скопирована');
    } catch (_) {
      setFeedback('Ошибка');
    }
    const t = setTimeout(() => setFeedback(''), 2000);
    return () => clearTimeout(t);
  }, [shareUrl]);

  const openTelegram = useCallback(() => {
    if (!shareUrl) return;
    const u = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title || '')}`;
    window.open(u, '_blank', 'noopener,noreferrer');
    closeMenu();
  }, [shareUrl, title, closeMenu]);

  const handleMore = useCallback(async () => {
    if (!shareUrl) return;
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ title: title || 'Вики', url: shareUrl });
        setFeedback('Отправлено');
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setFeedback('Ссылка скопирована');
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      copyLink();
    }
    closeMenu();
    const t = setTimeout(() => setFeedback(''), 2000);
    return () => clearTimeout(t);
  }, [shareUrl, title, copyLink, closeMenu]);

  if (!shareUrl) return null;

  return (
    <div className={styles.shareDropdownWrap} ref={menuRef}>
      <button
        type="button"
        className={className}
        onClick={() => setMenuOpen((o) => !o)}
        title="Поделиться"
        aria-label="Поделиться"
        aria-expanded={menuOpen}
        aria-haspopup="true"
      >
        <Share2 size={18} />
        <span>{feedback || 'Поделиться'}</span>
      </button>
      {menuOpen && (
        <div className={styles.shareDropdown} role="menu">
          <button
            type="button"
            className={styles.shareDropdownItem}
            onClick={() => { copyLink(); closeMenu(); }}
            role="menuitem"
            title="Скопировать ссылку"
            aria-label="Скопировать ссылку"
          >
            <Copy size={20} />
          </button>
          <button
            type="button"
            className={styles.shareDropdownItem}
            onClick={openTelegram}
            role="menuitem"
            title="Telegram"
            aria-label="Поделиться в Telegram"
          >
            <TelegramIcon size={20} />
          </button>
          <button
            type="button"
            className={styles.shareDropdownItem}
            onClick={handleMore}
            role="menuitem"
            title="Ещё"
            aria-label="Ещё способы поделиться"
          >
            <Share2 size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

const WikiPageHeader = ({
  title,
  breadcrumbs,
  author,
  updatedAt,
  views,
  formatDate = defaultFormatDate,
  shareUrl,
}) => {
  return (
    <div className={styles.pageHeader}>
      {breadcrumbs && <div className={styles.breadcrumbsWrap}>{breadcrumbs}</div>}
      <div className={styles.pageHeaderTop}>
        <h1 className={styles.pageTitle}>{title}</h1>
        {shareUrl && (
          <ShareButton
            shareUrl={shareUrl}
            title={title}
            className={styles.shareButton}
          />
        )}
      </div>
      <div className={styles.pageMeta}>
        <span className={styles.metaItem}>
          <User size={14} />
          {author || '—'}
        </span>
        <span className={styles.metaItem}>
          <Clock size={14} />
          {formatDate(updatedAt)}
        </span>
        <span className={styles.metaItem}>
          <Eye size={14} />
          {views ?? 0} просмотров
        </span>
      </div>
    </div>
  );
};

export default WikiPageHeader;
